import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, HelpCircle, Lightbulb, RotateCcw, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import {
  clearActivityScore,
  readWorkbookJson,
  writeActivityScore,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from './workbookPersist';

export interface RiddleChoiceCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  sourcePage?: number;
  prompt: string;
  riddles: string[];
  /** Shared word bank (same options shown for each riddle, order may vary per row). */
  wordBank: string[];
  /** Correct answer per riddle index; must align 1:1 with `riddles`. */
  correctAnswers: string[];
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type PersistShape = { picks: Record<number, string> };

function norm(s: string): string {
  let t = String(s).trim().replace(/\s+/g, ' ');
  try {
    t = t.normalize('NFKC');
  } catch {
    /* ignore */
  }
  return t;
}

function isMatch(a: string, b: string): boolean {
  return norm(a) === norm(b);
}

/** Deterministic shuffle so option order differs per riddle but is stable across renders. */
function seededShuffle(strings: string[], seed: number): string[] {
  const a = [...strings];
  let s = Math.abs(seed) + 1;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 16807) % 2147483647;
    const j = s % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ROW_ACCENT = [
  'border-l-4 border-l-blue-500 bg-blue-50/40 dark:border-l-blue-400 dark:bg-blue-950/20',
  'border-l-4 border-l-violet-500 bg-violet-50/40 dark:border-l-violet-400 dark:bg-violet-950/20',
  'border-l-4 border-l-amber-500 bg-amber-50/40 dark:border-l-amber-400 dark:bg-amber-950/20',
  'border-l-4 border-l-teal-500 bg-teal-50/40 dark:border-l-teal-400 dark:bg-teal-950/20',
] as const;

export const RiddleChoiceCard: React.FC<RiddleChoiceCardProps> = ({
  workbookLabel,
  title,
  instructions,
  sourcePage,
  prompt,
  riddles,
  wordBank,
  correctAnswers,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-rc-${persistenceId}` : '';

  const riddlesRef = useRef(riddles);
  riddlesRef.current = riddles;
  const riddlesStableKey = riddles.join('\u0001');

  const n = riddles.length;
  const valid =
    n > 0 &&
    wordBank.length > 0 &&
    correctAnswers.length === n &&
    correctAnswers.every((ans) => wordBank.some((w) => isMatch(w, ans)));

  const initialPicks = useMemo(() => {
    const m: Record<number, string> = {};
    riddles.forEach((_, i) => {
      m[i] = '';
    });
    return m;
  }, [riddlesStableKey, n]);

  const [picks, setPicks] = useState<Record<number, string>>(initialPicks);

  useEffect(() => {
    const empty: Record<number, string> = {};
    riddlesRef.current.forEach((_, i) => {
      empty[i] = '';
    });

    if (!storageKey || !valid) {
      setPicks(empty);
      return;
    }
    const loaded = readWorkbookJson<Partial<PersistShape> | null>(storageKey, null);
    if (loaded?.picks && typeof loaded.picks === 'object') {
      const merged = { ...empty };
      const raw = loaded.picks as Record<string, unknown>;
      for (const k of Object.keys(raw)) {
        const i = Number(k);
        if (!Number.isFinite(i) || merged[i] === undefined) continue;
        merged[i] = String(raw[k] ?? '');
      }
      setPicks(merged);
    } else {
      setPicks(empty);
    }
  }, [storageKey, riddlesStableKey, valid, n]);

  useEffect(() => {
    if (!storageKey || !valid) return;
    writeWorkbookJson(storageKey, { picks } satisfies PersistShape);
  }, [storageKey, valid, picks]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const outcomes = useMemo(() => {
    return riddles.map((_, i) => {
      const p = picks[i] ?? '';
      if (!norm(p)) return 'none' as const;
      return isMatch(p, correctAnswers[i] ?? '') ? ('correct' as const) : ('wrong' as const);
    });
  }, [riddles, picks, correctAnswers]);

  const correctCount = outcomes.filter((o) => o === 'correct').length;
  const allCorrect = valid && n > 0 && outcomes.every((o) => o === 'correct');

  const allPicked = valid && n > 0 && riddles.every((_, i) => norm(picks[i] ?? '').length > 0);

  useEffect(() => {
    if (!onWorkbookStatusChange || !valid) {
      onWorkbookStatusChange?.('not_started');
      return;
    }
    if (allPicked) {
      if (persistenceId) writeActivityScore(persistenceId, (correctCount / n) * 100);
      onWorkbookStatusChange('completed');
      return;
    }
    const any = riddles.some((_, i) => norm(picks[i] ?? '').length > 0);
    onWorkbookStatusChange(any ? 'in_progress' : 'not_started');
  }, [onWorkbookStatusChange, valid, allPicked, riddles, picks, persistenceId, correctCount, n]);

  const shuffledOptionsByRow = useMemo(
    () => riddles.map((text, i) => seededShuffle(wordBank, i * 7919 + text.length)),
    [riddles, wordBank]
  );

  const reset = () => {
    mark();
    setPicks({ ...initialPicks });
    if (storageKey && valid) writeWorkbookJson(storageKey, { picks: initialPicks } satisfies PersistShape);
    if (persistenceId) clearActivityScore(persistenceId);
    onWorkbookStatusChange?.('not_started');
  };

  const setPick = (index: number, word: string) => {
    mark();
    setPicks((prev) => {
      if (norm(prev[index] ?? '').length > 0) return prev;
      return { ...prev, [index]: word };
    });
  };

  if (!valid) {
    return (
      <div className="overflow-hidden rounded-[2rem] border-4 border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-900 dark:bg-rose-950/40">
        <p className="text-lg font-bold text-rose-900 tamil-text dark:text-rose-100" lang="ta">
          நொடி விடுகதை தரவு முழுமையடையவில்லை: riddles, word_bank, riddle_answers எண் சமநிலையில் இருக்க வேண்டும். JSON-ஐச் சரிபார்க்கவும்.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-amber-200/90 bg-gradient-to-br from-amber-50 via-orange-50/60 to-violet-50 shadow-[0_22px_55px_-14px_rgba(245,158,11,0.28)] dark:border-amber-900/45 dark:from-amber-950/35 dark:via-slate-900 dark:to-violet-950/25">
      <div className="border-b border-amber-200/70 bg-white/60 px-5 py-5 backdrop-blur-sm dark:border-amber-900/40 dark:bg-slate-900/55">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {workbookLabel ? (
              <p className="text-sm font-extrabold text-amber-900 tamil-text dark:text-amber-200" lang="ta">
                {workbookLabel}
              </p>
            ) : null}
            <h2 className="mt-1 flex items-center gap-2 text-2xl font-extrabold text-slate-900 tamil-text md:text-3xl dark:text-amber-50" lang="ta">
              <Sparkles className="h-8 w-8 shrink-0 text-amber-500" aria-hidden />
              {title}
            </h2>
            {instructions ? (
              <p className="mt-2 text-lg font-semibold leading-relaxed text-slate-800 tamil-text dark:text-slate-200" lang="ta">
                {instructions}
              </p>
            ) : null}
            {sourcePage != null ? (
              <span className="mt-3 inline-block rounded-full bg-amber-600 px-3 py-1 text-sm font-bold text-white shadow-sm">
                p.{sourcePage}
              </span>
            ) : null}
          </div>
          <div
            className="shrink-0 rounded-2xl border-2 border-violet-200 bg-violet-100/90 px-4 py-2 text-center dark:border-violet-700 dark:bg-violet-950/50"
            role="status"
            aria-live="polite"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-violet-800 dark:text-violet-300">முன்னேற்றம்</p>
            <p className="text-lg font-black text-violet-950 tamil-text dark:text-violet-50" lang="ta">
              {correctCount} / {n} சரி
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6 px-4 py-6 sm:px-8">
        <div
          className="rounded-2xl border-2 border-amber-300/80 bg-gradient-to-r from-amber-100 to-orange-100 px-5 py-4 text-center text-lg font-extrabold leading-snug text-amber-950 shadow-sm tamil-text dark:border-amber-800 dark:from-amber-950/50 dark:to-orange-950/40 dark:text-amber-50"
          lang="ta"
        >
          {prompt}
        </div>

        <div className="rounded-2xl border-2 border-violet-200/80 bg-white/70 p-4 dark:border-violet-900/50 dark:bg-slate-900/50">
          <div className="mb-2 flex items-center gap-2 text-violet-900 dark:text-violet-200">
            <HelpCircle className="h-5 w-5 shrink-0" aria-hidden />
            <p className="text-sm font-black tamil-text" lang="ta">
              ஒவ்வொரு நொடிக்கும் கீழே உள்ள நான்கு சொற்களில் ஒன்றைத் தட்டவும்.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-400">
            <Lightbulb className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
            <span className="tamil-text" lang="ta">
              ஒவ்வொரு நொடிக்கும் ஒரு தேர்வு மட்டும். தவறானால் மீண்டும் முயல் பொத்தானால் முழுச் செயலையும் மீட்டமைத்துப் பின் முயலவும்.
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {riddles.map((text, i) => {
            const o = outcomes[i];
            const chosen = picks[i] ?? '';
            const shuffled = shuffledOptionsByRow[i] ?? wordBank;

            return (
              <article
                key={i}
                className={clsx(
                  'overflow-hidden rounded-2xl border-2 border-slate-200 bg-white/95 shadow-md dark:border-slate-600 dark:bg-slate-900/90',
                  ROW_ACCENT[i % ROW_ACCENT.length],
                  o === 'correct' && 'ring-2 ring-emerald-300/80 ring-offset-2 ring-offset-amber-50 dark:ring-emerald-600/50 dark:ring-offset-slate-900',
                  o === 'wrong' && 'ring-2 ring-rose-200/90 ring-offset-2 ring-offset-amber-50 dark:ring-rose-600/40 dark:ring-offset-slate-900'
                )}
              >
                <div className="border-b border-slate-100/90 px-4 py-3 dark:border-slate-700">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-full bg-slate-900 px-2 text-sm font-black text-white dark:bg-slate-100 dark:text-slate-900">
                      {i + 1}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400" lang="ta">
                      நொடி {i + 1} / {n}
                    </span>
                    {o === 'correct' ? (
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-black text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" lang="ta">
                        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                        சரி
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-3 text-lg font-black leading-relaxed text-slate-900 tamil-text sm:text-xl dark:text-slate-100" lang="ta">
                    {text}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4 sm:gap-3">
                  {shuffled.map((word) => {
                    const isChosen = isMatch(chosen, word);
                    const rowLocked = norm(chosen).length > 0;
                    return (
                      <button
                        key={`${i}-${word}`}
                        type="button"
                        disabled={rowLocked && !isChosen}
                        onClick={() => setPick(i, word)}
                        className={clsx(
                          'min-h-[48px] rounded-2xl border-2 px-2 py-2.5 text-center text-base font-black leading-snug shadow-sm transition active:scale-[0.98] tamil-text sm:min-h-[52px] sm:text-lg',
                          isChosen && o === 'correct'
                            ? 'border-emerald-500 bg-emerald-100 text-emerald-950 ring-2 ring-emerald-400/80 dark:border-emerald-400 dark:bg-emerald-900/45 dark:text-emerald-50'
                            : isChosen && o === 'wrong'
                              ? 'border-rose-500 bg-rose-100 text-rose-950 ring-2 ring-rose-400/70 dark:border-rose-400 dark:bg-rose-900/40 dark:text-rose-50'
                              : isChosen
                                ? 'border-amber-400 bg-amber-50 text-slate-900 dark:border-amber-500 dark:bg-amber-950/30 dark:text-amber-50'
                                : 'border-slate-200 bg-white text-slate-800 hover:border-violet-300 hover:bg-violet-50/80 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-violet-500'
                        )}
                        aria-pressed={isChosen}
                        lang="ta"
                      >
                        {word}
                      </button>
                    );
                  })}
                </div>
              </article>
            );
          })}
        </div>

        {allCorrect ? (
          <p className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-emerald-300 bg-emerald-50/95 px-4 py-4 text-center text-base font-black text-emerald-900 tamil-text dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100" lang="ta">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" aria-hidden />
            அருமை! எல்லா நொடிகளும் சரியாக விடுவிக்கப்பட்டன.
          </p>
        ) : null}

        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={reset}
            className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-2 border-slate-300 bg-white px-8 py-3 text-base font-extrabold text-slate-800 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 tamil-text"
            lang="ta"
          >
            <RotateCcw className="h-5 w-5 shrink-0" aria-hidden />
            மீண்டும் முயல்
          </button>
        </div>
      </div>
    </div>
  );
};
